import React, {Fragment, useCallback, useEffect, useRef, useState} from "react";
import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';
import makeStyles from '@mui/styles/makeStyles';
import {color, font, radius, shadow, type} from "common/tokens";

const LIST_MAX_HEIGHT = 260;
const LIST_GAP = 6;
// Over the dialogs this field sits in, which stop at 1400.
const LIST_Z_INDEX = 1500;

const useStyles = makeStyles({
  phoneInputWrapper: {
    '& .react-tel-input': {
      fontFamily: font.SANS,
    },
    '& .form-control': {
      ...type.body,
      width: '100%',
      height: 52,
      background: color.SURFACE,
      color: color.TEXT,
      borderRadius: 12,
      border: `1px solid ${color.BORDER}`,
      paddingLeft: 60,
      boxShadow: 'none',
    },
    '& .form-control:focus': {
      borderColor: color.ACCENT,
      outline: 'none',
      boxShadow: 'none',
    },
    // The country list is a child of the flag button, which is only 48px wide,
    // so a width of 100% sized the list to the button rather than the field.
    // Taking the button out of the positioned flow hands the list's containing
    // block to the wrapper, and the button is placed over the field instead.
    '& .flag-dropdown, & .flag-dropdown.open': {
      position: 'static',
      background: 'none',
      border: 'none',
      borderRadius: 0,
    },
    '& .selected-flag, & .selected-flag:hover, & .selected-flag:focus, & .flag-dropdown.open .selected-flag': {
      position: 'absolute',
      top: 9,
      left: 0,
      height: 34,
      background: 'none',
      borderRight: `1px solid ${color.DIVIDER}`,
      borderRadius: 0,
      width: 48,
      paddingLeft: 14,
      boxSizing: 'border-box',
    },
    '& .selected-flag .arrow': {
      borderTopColor: color.TEXT_MUTED,
    },
    '& .selected-flag .arrow.up': {
      borderBottomColor: color.TEXT_MUTED,
    },
    // Taken out of the flow entirely and placed against the field, because the
    // forms this field appears in sit inside dialogs that scroll their body:
    // an absolutely positioned list is clipped by that body, and in a short
    // dialog there is no room either side of the field to flip into. Fixed,
    // it is bounded by the viewport instead. The position comes from
    // positionList below, as custom properties on the wrapper.
    '& .country-list': {
      position: 'fixed',
      top: 'var(--phone-list-top, auto)',
      bottom: 'var(--phone-list-bottom, auto)',
      left: 'var(--phone-list-left, 0)',
      width: 'var(--phone-list-width, 100%)',
      maxHeight: 'var(--phone-list-max, 260px)',
      zIndex: LIST_Z_INDEX,
      margin: 0,
      padding: 6,
      boxSizing: 'border-box',
      background: color.SURFACE,
      border: `1px solid ${color.DIVIDER}`,
      borderRadius: radius.PANEL,
      boxShadow: shadow.POPOVER,
    },
    // Sticky already, but without a stacking order the rows scrolled over it.
    '& .country-list .search': {
      padding: 6,
      background: color.SURFACE,
      zIndex: 1,
    },
    '& .country-list .search-box': {
      ...type.body,
      width: '100%',
      margin: 0,
      padding: '10px 12px',
      boxSizing: 'border-box',
      border: `1px solid ${color.BORDER}`,
      borderRadius: 10,
      color: color.TEXT,
    },
    '& .country-list .search-box:focus': {
      borderColor: color.ACCENT,
      outline: 'none',
    },
    '& .country-list .country': {
      ...type.body,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 12px',
      borderRadius: 8,
      color: color.TEXT,
    },
    '& .country-list .country .dial-code': {
      color: color.TEXT_MUTED,
    },
    '& .country-list .country:hover, & .country-list .country.highlight': {
      background: color.SURFACE_MUTED,
    },
    '& .country-list .country.highlight .dial-code': {
      color: color.TEXT_MUTED,
    },
    '& .country-list .no-entries-message': {
      ...type.body,
      color: color.TEXT_MUTED,
    },
    '& .country-list::-webkit-scrollbar': {
      width: 8,
    },
    '& .country-list::-webkit-scrollbar-thumb': {
      background: color.BORDER,
      borderRadius: 4,
    },
  },
});

const countryPhoneLengths = {
  in: 12, us: 11, ca: 11, gb: 12, au: 12, nz: 11, sg: 10, ae: 12, sa: 12,
  fr: 11, de: 13, it: 12, es: 11, nl: 11, be: 11, ch: 11, cn: 13, jp: 12,
  kr: 12, br: 13, ar: 12, mx: 12, za: 11, ng: 13, eg: 12, pk: 12, bd: 13,
  ru: 12, tr: 12, id: 13, th: 11, my: 11,
};

const PhoneNumberInput = ({onAnswered, value, error, id, describedBy}) => {
  const classes = useStyles();
  const wrapper = useRef(null);
  const [listBox, setListBox] = useState(null);

  // The library has no notion of available space, so the list is placed here:
  // under the field by default, over it when there is more room that way, and
  // never taller than the space it has. Measured from the field alone, so it
  // does not have to wait for the list to be rendered.
  const positionList = useCallback(() => {
    const el = wrapper.current;
    if (!el) return;

    const field = el.getBoundingClientRect();
    const below = window.innerHeight - field.bottom - LIST_GAP;
    const above = field.top - LIST_GAP;
    const dropUp = below < LIST_MAX_HEIGHT && above > below;

    setListBox({
      '--phone-list-left': `${field.left}px`,
      '--phone-list-width': `${field.width}px`,
      '--phone-list-max': `${Math.min(LIST_MAX_HEIGHT, dropUp ? above : below)}px`,
      '--phone-list-top': dropUp ? 'auto' : `${field.bottom + LIST_GAP}px`,
      '--phone-list-bottom': dropUp ? `${window.innerHeight - field.top + LIST_GAP}px` : 'auto',
    });
  }, []);

  // A fixed list does not move with the field, so it is re-placed whenever
  // anything scrolls it — capture, to catch the dialog body's own scroll.
  useEffect(() => {
    window.addEventListener('scroll', positionList, true);
    window.addEventListener('resize', positionList);
    return () => {
      window.removeEventListener('scroll', positionList, true);
      window.removeEventListener('resize', positionList);
    };
  }, [positionList]);

  const handleChange = (phone, country) => {
    const digits = phone.replace(/\D/g, '');
    const limit = countryPhoneLengths[country && country.countryCode];

    onAnswered(limit ? digits.slice(0, limit) : digits);
  };

  return (
    <Fragment>
      <div
        ref={wrapper}
        className={classes.phoneInputWrapper}
        style={listBox || undefined}
        onClick={positionList}
      >
        <PhoneInput
          country={'in'}
          value={value}
          onChange={handleChange}
          disableFormatting={true}
          enableSearch={true}
          inputProps={{
            name: 'phone',
            required: true,
            id,
            'aria-describedby': describedBy,
            'aria-invalid': Boolean(error) || undefined,
          }}
          masks={{'in': '..........'}}
        />
      </div>
    </Fragment>
  );
};

export default PhoneNumberInput;
